/**
 * UserSync Page
 *
 * Displays OMHAM CSV sync data with search and pagination.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Database } from "lucide-react";
import { userSyncService, type UserSyncItem, type UserSyncSortField } from "@/services/userSyncService";

const PAGE_SIZE = 20;

const SORT_OPTIONS: { value: UserSyncSortField; label: string }[] = [
  { value: "callsign", label: "Callsign" },
  { value: "namaPemilik", label: "Nama Pemilik" },
  { value: "sourceId", label: "ID / Source" },
  { value: "provinsi", label: "Provinsi" },
  { value: "kabupaten", label: "Kabupaten" },
  { value: "email", label: "Email" },
  { value: "status", label: "Status" },
  { value: "grossAmount", label: "Gross Amount" },
  { value: "settlementDate", label: "Settlement Date" },
];

export function UserSyncPage() {
  const [data, setData] = React.useState<UserSyncItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [sortBy, setSortBy] = React.useState<UserSyncSortField>("callsign");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await userSyncService.getUserSyncList({
        page,
        limit: PAGE_SIZE,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
      });
      setData(res.data);
      setTotal(res.total);
    } catch (err) {
      console.error("UserSync fetch error:", err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, sortBy, sortOrder]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  React.useEffect(() => {
    document.title = "Data Sync OMHAM - ORARI";
  }, []);

  return (
    <div className="flex-1 space-y-4 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Data Sync OMHAM</h1>
        <p className="text-muted-foreground">
          Data sementara dari CSV OMHAM (user_sync)
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Daftar User Sync
          </CardTitle>
          <CardDescription>
            Total: {total} baris. Halaman {page} dari {totalPages}.
          </CardDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Input
              placeholder="Cari callsign, nama, email, provinsi..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="max-w-sm"
            />
            <Button onClick={handleSearch} variant="secondary" size="icon" title="Cari">
              <Search className="h-4 w-4" />
            </Button>
            <Select
              value={sortBy}
              onValueChange={(v) => {
                setSortBy(v as UserSyncSortField);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={sortOrder}
              onValueChange={(v) => {
                setSortOrder(v as "asc" | "desc");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">A → Z</SelectItem>
                <SelectItem value="desc">Z → A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Tidak ada data. Jalankan seed:user-sync untuk mengimpor CSV.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[100px]">Callsign</TableHead>
                      <TableHead className="min-w-[200px]">Nama Pemilik</TableHead>
                      <TableHead className="min-w-[120px]">Provinsi</TableHead>
                      <TableHead className="min-w-[120px]">Kabupaten</TableHead>
                      <TableHead className="min-w-[180px]">Email</TableHead>
                      <TableHead className="min-w-[80px]">Status</TableHead>
                      <TableHead className="min-w-[140px]">Keanggotaan</TableHead>
                      <TableHead className="min-w-[100px]">NRI</TableHead>
                      <TableHead className="min-w-[100px]">Jenis</TableHead>
                      <TableHead className="min-w-[220px]">Payment ID</TableHead>
                      <TableHead className="min-w-[100px]">Gross Amount</TableHead>
                      <TableHead className="min-w-[90px]">Status Payment</TableHead>
                      <TableHead className="min-w-[140px]">Settlement Date</TableHead>
                      <TableHead className="min-w-[120px]">Masa Laku IAR</TableHead>
                      <TableHead className="min-w-[120px]">Masa Laku KTA</TableHead>
                      <TableHead className="min-w-[140px]">Expected Masa Laku KTA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell className="font-mono text-sm">{row.callsign ?? "-"}</TableCell>
                        <TableCell>{row.namaPemilik ?? "-"}</TableCell>
                        <TableCell>{row.provinsi ?? "-"}</TableCell>
                        <TableCell>{row.kabupaten ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.email ?? "-"}</TableCell>
                        <TableCell>{row.status ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.keanggotaan ?? "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{row.nri ?? "-"}</TableCell>
                        <TableCell>{row.jenis ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[220px] truncate" title={row.paymentId ?? undefined}>
                          {row.paymentId ?? "-"}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {row.grossAmount != null
                            ? new Intl.NumberFormat("id-ID", {
                                style: "currency",
                                currency: "IDR",
                                minimumFractionDigits: 0,
                              }).format(row.grossAmount)
                            : "-"}
                        </TableCell>
                        <TableCell>{row.statusPayment ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.settlementDate ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.masaLakuIar ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.masaLakuKta ?? "-"}</TableCell>
                        <TableCell className="text-sm">{row.expectedMasaLakuKta ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrev}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Sebelumnya
                </Button>
                <span className="text-sm text-muted-foreground">
                  Halaman {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Selanjutnya
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
